import { useEventBus } from "@vueuse/core";
import { useFirebase } from "@/composables/useFirebase";

const isPanelOpen = ref(false);
const isPanelExpanded = ref(false);
const isClosePanelConfirmationOpen = ref(false);

const addOpenPanelHookHooks = ref([]) as Ref<(() => void)[]>;
const addMinimizePanelHookHooks = ref([]) as Ref<(() => void)[]>;
const addExpandPanelHookHooks = ref([]) as Ref<(() => void)[]>;
const addCollapsePanelHookHooks = ref([]) as Ref<(() => void)[]>;
const addClosePanelHookHooks = ref([]) as Ref<(() => void)[]>;

export const usePanel = () => {
  const bus = useEventBus<string>("main");

  const { logEvent } = useFirebase();

  const openPanel = () => {
    // Log event to firebase
    logEvent("open_panel");
    // Open panel
    isPanelOpen.value = true;
    // Attach optional hooks
    addOpenPanelHookHooks.value.forEach((hook) => hook());
  };

  const minimizePanel = () => {
    // Log event to firebase
    logEvent("minimize_panel");
    // close panel
    isPanelOpen.value = false;
    // Attach optional hooks
    addMinimizePanelHookHooks.value.forEach((hook) => hook());
  };

  const expandPanel = () => {
    // Expand panel
    isPanelExpanded.value = true;
    // Attach optional hooks
    addExpandPanelHookHooks.value.forEach((hook) => hook());
  };

  const collapsePanel = () => {
    // Log event to firebase
    logEvent("collapse_panel");
    // Collapse panel
    isPanelExpanded.value = false;
    // Attach optional hooks
    addCollapsePanelHookHooks.value.forEach((hook) => hook());
  };

  const closePanel = () => {
    // Log event to firebase
    logEvent("close_panel");
    // Minimize panel
    minimizePanel();
    // Hide confirmation
    hideClosePanelConfirmation();
    // Collapse panel when transition has finished
    window.setTimeout(() => {
      collapsePanel();
    }, 500);
    // Attach optional hooks

    addClosePanelHookHooks.value.forEach((hook) => hook());
  };

  const showClosePanelConfirmation = () => {
    isClosePanelConfirmationOpen.value = true;
  };

  const hideClosePanelConfirmation = () => {
    isClosePanelConfirmationOpen.value = false;
  };

  const addOpenPanelHook = (callback: () => void) => {
    addOpenPanelHookHooks.value.push(callback);
  };

  const addMinimizePanelHook = (callback: () => void) => {
    addMinimizePanelHookHooks.value.push(callback);
  };

  const addExpandPanelHook = (callback: () => void) => {
    addExpandPanelHookHooks.value.push(callback);
  };

  const addCollapsePanelHook = (callback: () => void) => {
    addCollapsePanelHookHooks.value.push(callback);
  };

  const addClosePanelHook = (callback: () => void) => {
    addClosePanelHookHooks.value.push(callback);
  };

  const listener = (event: string) => {
    switch (event) {
      case "panel:open":
        openPanel();
        break;
      case "panel:close":
        showClosePanelConfirmation();
        break;
      case "panel:force-close":
        closePanel();
        break;
      case "panel:minimize":
        minimizePanel();
        break;
      case "panel:collapse":
        collapsePanel();
        break;
      case "panel:expand":
        expandPanel();
        break;
    }
  };

  onMounted(() => {
    bus.on(listener);
  });

  onUnmounted(() => {
    bus.off(listener);
  });

  return {
    isPanelOpen,
    isPanelExpanded,
    isClosePanelConfirmationOpen,
    openPanel,
    minimizePanel,
    expandPanel,
    collapsePanel,
    closePanel,
    showClosePanelConfirmation,
    hideClosePanelConfirmation,
    addOpenPanelHook,
    addMinimizePanelHook,
    addExpandPanelHook,
    addCollapsePanelHook,
    addClosePanelHook,
  };
};
